import React, { createContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  clearSession,
  getAuthToken,
  getSessionUser,
  getTokenExpirationMs,
} from "./utils/session";

export const AuthContext = createContext();

const SESSION_EXPIRED_MESSAGE =
  "El tiempo de la sesión activa ha caducado. Por seguridad, inicia sesión de nuevo.";

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(getAuthToken()));
  const navigate = useNavigate();
  const sessionHandledRef = useRef(false);
  const expirationTimeoutRef = useRef(null);

  const handleSessionExpired = (customMessage) => {
    if (sessionHandledRef.current) return;

    sessionHandledRef.current = true;
    clearSession();
    setIsAuthenticated(false);

    toast.warning(customMessage || SESSION_EXPIRED_MESSAGE, {
      position: "top-right",
      autoClose: 4500,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    setTimeout(() => {
      navigate("/login", {
        replace: true,
        state: {
          sessionExpired: true,
          message: customMessage || SESSION_EXPIRED_MESSAGE,
        },
      });

      window.sessionExpiredEventEmitted = false;
      sessionHandledRef.current = false;
    }, 500);
  };

  useEffect(() => {
    const onSessionExpired = (event) => {
      handleSessionExpired(event?.detail?.message || SESSION_EXPIRED_MESSAGE);
    };

    window.addEventListener("sessionExpired", onSessionExpired);
    return () => window.removeEventListener("sessionExpired", onSessionExpired);
  }, [navigate]);

  useEffect(() => {
    const scheduleExpirationCheck = () => {
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
      }

      const token = getAuthToken();
      const sessionUser = getSessionUser();
      setIsAuthenticated(Boolean(token && sessionUser?.id));

      if (!token) return;

      const expiresAtMs = getTokenExpirationMs(token);
      if (!expiresAtMs) return;

      const millisecondsUntilExpiration = expiresAtMs - Date.now();

      if (millisecondsUntilExpiration <= 0) {
        handleSessionExpired(SESSION_EXPIRED_MESSAGE);
        return;
      }

      expirationTimeoutRef.current = setTimeout(() => {
        handleSessionExpired(SESSION_EXPIRED_MESSAGE);
      }, millisecondsUntilExpiration + 250);
    };

    scheduleExpirationCheck();

    const onFocusOrVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleExpirationCheck();
      }
    };

    window.addEventListener("focus", scheduleExpirationCheck);
    window.addEventListener("dqpa:session-updated", scheduleExpirationCheck);
    document.addEventListener("visibilitychange", onFocusOrVisibilityChange);

    return () => {
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
      }
      window.removeEventListener("focus", scheduleExpirationCheck);
      window.removeEventListener("dqpa:session-updated", scheduleExpirationCheck);
      document.removeEventListener("visibilitychange", onFocusOrVisibilityChange);
    };
  }, [navigate]);

  const logout = () => {
    clearSession();
    setIsAuthenticated(false);
    navigate("/login", { replace: true });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
